import fp from 'lodash/fp';
import React, { ComponentProps, FC, useCallback, useState } from 'react';

import { animation, Button, Portal, TransitionComponent } from 'Modules/ui2';

import { useRequest } from '../../hooks';
import { LeadResult } from '../../Lead';
import { Tariff } from '../Tariffs';
import { RateProps } from '../types';
import { ErrorBanner } from './ErrorBanner';


const mapLeadForRequest = (data: LeadResult) => ({
    ...fp.omit(['Uid', 'LeadGeneratorName', 'IsMobile'], data),
    IsMobile: true,
});

type ShowErrorButtonProps = ComponentProps<Tariff['button']> & {
    type: string;
}

export const ShowErrorButton: FC<ShowErrorButtonProps> = ({ t, type, leadResult }) => {
    const [isShow, showError] = useState(false);

    const { request } = useRequest<LeadResult>(`/leads/${leadResult.Uid}`, mapLeadForRequest, 'PUT');
    const sendMobileLead = useCallback(
        () => request({ ...leadResult, TariffTypeCode: type }),
        [leadResult, type, request],
    );
    const showErrorWithSendLead = useCallback(
        () => {
            sendMobileLead();
            showError(true);
        },
        [sendMobileLead],
    );

    return (
        <>
            <Button
                variant='normal'
                size='56'
                onClick={showErrorWithSendLead}
            >
                {t('rate.buttons.continue')}
            </Button>
            <Portal>
                <TransitionComponent in={isShow} timeout={animation}>
                    <ErrorBanner t={t} onClose={() => showError(false)} />
                </TransitionComponent>
            </Portal>
        </>
    );
};

export const ContinueButton: Tariff['button'] = ({ t, setStage, className, values }) => {
    const showForm = useCallback(
        () => setStage('cryptoProImport', { values: fp.omit('usePersonalData', values) }),
        [values, setStage],
    );
    return (
        <Button
            variant='normal'
            size='56'
            className={className}
            onClick={showForm}
        >
            {t('rate.buttons.continue')}
        </Button>
    );
};

const mapFormValuesToApi = (data: RateProps['values']) => ({
    Surname: data.surname,
    FirstName: data.firstName,
    CompanyName: data.company.value,
    CompanyINN: data.company.data.inn,
    CompanyOGRN: data.company.data.ogrn,
    CompanyKPP: data.company.data.kpp,
    Email: data.email,
    Email2: data.email2 || '',
    PhoneNumber: data.phone,
    AllowUsePersonalData: true,
});

const INNFailureMessage = 'CompanyINN:Компания с таким ИНН уже зарегистрирована';

export const RegisterButton: Tariff['button'] = ({ t, setStage, values, className }) => {
    const onSuccess = useCallback(
        (values, data) => data.Created
            ? setStage('successLite')
            : setStage('resendLetter', {
                email: values.email,
                timer: data.ConfirmCodeRequestRemaining,
            }),
        [setStage],
    );
    const onReject = useCallback(
        error => {
            const message = fp.get('message', error);
            if (message === INNFailureMessage)
                setStage('failureINNLite');
            else
                setStage('failureLite', { error: message || t('failureLite.unknown') });
        },
        [setStage, t],
    );
    const { busy, request } = useRequest<RateProps['values']>(
        '/registration/createlight',
        mapFormValuesToApi,
        'POST',
        onSuccess,
        onReject,
    );

    const sendRequest = useCallback(
        () => request(values),
        [request, values],
    );
    return (
        <Button
            variant='normal'
            size='56'
            className={className}
            disabled={busy}
            loading={busy}
            onClick={sendRequest}
        >
            {t('rate.buttons.register')}
        </Button>
    );
};
